import Vue from 'vue'
import axios from '@/libs/axios'

import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import {
  checkHiddenAttr,
  designateOptions,
  designateValue,
  hasValue,
  transformField,
} from '@/helpers/formConditions'

const defaultState = () => {
  return {
    newChildForm: {},
    userAttributes: [],
    preparedFields: [],
    allConditions: [],
    hasFileField: false,
    hasSignatureField: false,
    isLoading: false,
    validTracker: false,
    familyName: '',
    defaultGender: null,
    serverMessage: '',
    errorMessage: '',
    validationErrors: [],
    disabledInfoMessage: '',
    isSendButtonDisabled: false,
  }
}

export default {
  namespaced: true,
  state: defaultState(),
  getters: {
    getIsLoading: state => state.isLoading,
    getNewChildForm: state => state.newChildForm,
    getHasFileField: state => state.hasFileField,
    getHasSignatureField: state => state.hasSignatureField,
    getPreparedFields: state => state.preparedFields,
    getAllConditions: state => state.allConditions,
    getValidTracker: state => state.validTracker,
    getServerMessage: state => state.serverMessage,
    getErrorMessage: state => state.errorMessage,
    getValidationErrors: state => state.validationErrors,
    getFamilyName: state => state.familyName,
    getDefaultGender: state => state.defaultGender,
    getChildData: state => state.childData,
    getUserAttributes: state => state.userAttributes,
    getDisabledInfoMessage: state => state.disabledInfoMessage,
    getIsSendButtonDisabled: state => state.isSendButtonDisabled,
  },
  mutations: {
    SET_IS_LOADING: (state, data) => {
      state.isLoading = data
    },
    SET_VALID_TRACKER: (state, data) => {
      state.validTracker = data
    },
    SET_SERVER_MESSAGE: (state, data) => {
      state.serverMessage = data
    },
    SET_ERROR_MESSAGE: (state, data) => {
      state.errorMessage = data
    },
    SET_VALIDATION_ERRORS: (state, data) => {
      state.validationErrors = data
    },
    SET_FAMILY_NAME: (state, data) => {
      state.familyName = data
    },
    SET_DEFAULT_GENDER: (state, data) => {
      state.defaultGender = data
    },
    SET_USER_ATTRIBUTES: (state, data) => {
      state.userAttributes = data
    },
    RESET_DATA: state => {
      state.hasSignatureField = false
      state.hasSignatureField = false
      state.userAttributes = []
      state.preparedFields = []
      state.allConditions = []
      state.validTracker = false
      state.serverMessage = ''
      state.serverMessage = ''
    },
    SET_FORMS_DATA: state => {
      let index = 0
      state.preparedFields = state.newChildForm.fields.map(field => {
        if (['avatar', 'file_upload'].includes(field.type)) state.hasFileField = true
        if (field.type === 'signature') state.hasSignatureField = true

        const ref = field.name + index
        index++

        if (field.attribute_field_name === '1#last_name') {
          field.default = state.familyName
        }

        if (field.attribute_field_name === '1#gender' && state.defaultGender) {
          field.default = state.defaultGender
        }

        return {
          id: field.id,
          ref: ref,
          value: designateValue(field.type, field.default, field.options),
          label: field.label,
          name: field.name,
          settings: field.settings,
          options: designateOptions(field.type, field.options),
          type: transformField(field.type),
          required: field.required,
          hidden: checkHiddenAttr(field.conditions.filter(item => item.action === 'Show')),
          use_prefill: field.use_prefill,
          attribute_field_name: field.attribute_field_name,
          clear_attribute_field_name: field.clear_attribute_field_name,
          readonly: field.readonly,
          password_repeat: null,
        }
      })

      state.allConditions =  state.newChildForm.conditions
    },
    SET_FORMS_VALUES_FROM_USER_ATTRIBUTES: state => {
      state.preparedFields.forEach(field => {
        // Skip prefill for fields where use_prefill is explicitly disabled
        if (field.use_prefill === false) return

        state.userAttributes.forEach(item => {
          if (field.name === item.key || field.clear_attribute_field_name === item.key) {
            if (field.type === 'file_upload' || field.type === 'avatar') {
              field.value = item.value
              field.files = { 'url': item.value }
            } else {
              field.value = designateValue(field.type, item.value, field.options)
            }
          }
        })
      })
    },
    CHECK_CONDITIONS: state => {
      state.allConditions.forEach(condition => {
        let isMatched
        for (let i = 0; i < condition.scoped_conditions.length; i++) {
          const scopeItems = condition.scoped_conditions[i];
          const scopeItemsResults = scopeItems.map(item => {
            const foundedField = state.preparedFields.find(field => field.id === item.field_id)
            return hasValue(item, foundedField.value)
          })
          isMatched = scopeItemsResults.every(i => i === true)
          if (isMatched || i == condition.scoped_conditions.length - 1) {

            if (condition.related_type === 'Field') {
              const field = state.preparedFields.find(field => field.id === condition.related_id)

              if (field) {
                field.hidden = condition.action === 'Show' ? !isMatched : isMatched
                if (!isMatched) {
                  field.value = designateValue(field.type, field.value, field.options)
                }
              }
            }

            if (condition.action === 'disable_submission') {
              state.disabledInfoMessage = null
              const infoMessage = condition.settings.find(s => s.key === 'info_message')
              if (isMatched && infoMessage.value) {
                state.disabledInfoMessage = infoMessage.value
              }

              state.isSendButtonDisabled = !!isMatched
            }
            break
          }
        }
      })
    },
    SET_NEW_CHILD_FORM: (state, data) => {
      state.newChildForm = data
    },
  },
  actions: {
    fetchSchoolNewChildForm(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get(`school-registration-forms/${queryParams.role}`, {
            params: queryParams.query,
            headers: {
              WithoutSchoolFilter: true,
            }
          })
          .then(response => {
            ctx.commit('SET_NEW_CHILD_FORM', response.data.data.form)
            resolve(response)
          })
          .catch(error => reject(error))
      })
    },
    fetchChildEditData(ctx, id) {
      return new Promise((resolve, reject) => {
        axios
          .get(`/my-children/${id}/edit`)
          .then(response => {
            ctx.commit('SET_CHILD_DATA', response.data.data)
            resolve(response)
          })
          .catch(error => reject(error))
      })
    },
    fetchUserAttributes(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/user-attributes', {
            params: queryParams,
          })
          .then(response => {
            ctx.commit('SET_USER_ATTRIBUTES', response.data.data)
            resolve(response)
          })
          .catch(error => reject(error))
      })
    },
    updateChildInfo(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .put(`/my-children/${queryParams.user_id}`, queryParams.query)
          .then(response => {
            Vue.$toast({
              component: ToastificationContent,
              props: {
                title: "The User Data was successfully updated",
                icon: 'ThumbsUpIcon',
                variant: 'info',
              },
            })
            resolve(response)
          })
          .catch(error => reject(error))
      })
    },
    createOrUpdateChild(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
            .post(`/my-children/`, queryParams)
          .then(response => {
            Vue.$toast({
              component: ToastificationContent,
              props: {
                title: "The Child was successfully created",
                icon: 'ThumbsUpIcon',
                variant: 'info',
              },
            })
            resolve(response)
          })
          .catch(error => reject(error))
      })
    },
  },
}